<div class="template-detail-container">
  <!-- LOADING STATE -->
  <div *ngIf="isLoading" class="loading-state card-style">
    <div class="loading-content">
      <p-progressSpinner 
        strokeWidth="4" 
        animationDuration="1s"
        [style]="{ width: '60px', height: '60px' }">
      </p-progressSpinner>
      <p>Cargando plantilla...</p>
    </div>
  </div>

  <!-- ERROR STATE -->
  <div *ngIf="hasError && !isLoading" class="no-data-container">
    <div class="no-data-card">
      <i class="pi pi-exclamation-triangle" style="font-size: 3rem; color: #d21313;"></i>
      <h3>Error al cargar la plantilla</h3>
      <p>{{ errorMessage }}</p>
      <p-button 
        label="Volver" 
        icon="pi pi-arrow-left" 
        (onClick)="goBack()"
        styleClass="p-button-secondary">
      </p-button>
    </div>
  </div>

  <!-- CONTENT STATE -->
  <ng-container *ngIf="!isLoading && !hasError && template">
    <!-- HEADER PRINCIPAL -->
    <div class="card-style template-main-header">
      <div class="template-title-row">
        <div class="template-identity">
          <div class="template-icon">
            <i class="fa-solid fa-file-lines"></i>
          </div>
          
          <div class="template-title-content">
            <h1 class="template-name">{{ template.name }}</h1>
          </div>
        </div>

        <div class="template-actions">
          <p-button 
            label="Volver" 
            icon="pi pi-arrow-left" 
            (onClick)="goBack()"
            styleClass="p-button-outlined p-button-secondary">
          </p-button>
          <p-button 
            label="Editar" 
            icon="pi pi-pencil" 
            (onClick)="editTemplate()"
            styleClass="p-button-primary">
          </p-button>
        </div>
      </div>

      <div class="template-status-row">
        <div class="template-status-badge" [ngClass]="getStatusClass()">
          <i [class]="getStatusIcon()"></i>
          <span>{{ getStatusLabel() }}</span>
        </div>

        <div class="channels-badge">
          <i class="fa-solid fa-tower-broadcast"></i>
          <span>{{ getConfiguredChannelsCount() }} canal(es) configurado(s)</span>
        </div>
      </div>

      <div *ngIf="template.description" class="template-description">
        <i class="fa-solid fa-info-circle"></i>
        <p>{{ template.description }}</p>
      </div>
    </div>

    <!-- GRID DE INFORMACIÓN BÁSICA -->
    <div class="template-info-grid">
      <!-- VARIABLES DISPONIBLES -->
      <div class="card-style info-card">
        <div class="card-header">
          <i class="fa-solid fa-brackets-curly"></i>
          <h3>Variables Disponibles</h3>
        </div>
        <div class="card-content">
          <div class="variables-list">
            <div *ngFor="let variable of template.variables" class="variable-chip">
              <i class="fa-solid fa-code"></i>
              <code>{{'{{' + variable + '}}'}}</code>
            </div>
          </div>
          <div *ngIf="!template.variables || template.variables.length === 0" class="empty-state-mini">
            <i class="fa-solid fa-circle-info"></i>
            <span>No hay variables definidas</span>
          </div>
        </div>
      </div>

      <!-- INFORMACIÓN DE AUDITORÍA -->
      <div class="card-style info-card">
        <div class="card-header">
          <i class="fa-solid fa-clock-rotate-left"></i>
          <h3>Auditoría</h3>
        </div>
        <div class="card-content">
          <div class="audit-timeline">
            <div class="audit-item">
              <div class="audit-marker created-marker">
                <i class="fa-solid fa-plus"></i>
              </div>
              <div class="audit-content">
                <span class="audit-label">Creada</span>
                <span class="audit-value">{{ formatDate(template.createdAt) }}</span>
              </div>
            </div>

            <div class="audit-item">
              <div class="audit-marker updated-marker">
                <i class="fa-solid fa-pen"></i>
              </div>
              <div class="audit-content">
                <span class="audit-label">Última actualización</span>
                <span class="audit-value">{{ formatDate(template.updatedAt) }}</span>
              </div>
            </div>

            <div *ngIf="template.deletedAt" class="audit-item">
              <div class="audit-marker deleted-marker">
                <i class="fa-solid fa-trash"></i>
              </div>
              <div class="audit-content">
                <span class="audit-label">Eliminada</span>
                <span class="audit-value">{{ formatDate(template.deletedAt) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- CANALES CONFIGURADOS -->
    <div class="channels-section">
      <h2 class="section-title">
        <i class="fa-solid fa-tower-broadcast"></i>
        Canales Configurados
      </h2>

      <!-- EMAIL TEMPLATE -->
      <div *ngIf="hasEmailTemplate()" class="card-style channel-card email-card">
        <div class="channel-header">
          <div class="channel-title">
            <div class="channel-icon email-icon">
              <i class="fa-solid fa-envelope"></i>
            </div>
            <h3>Email</h3>
            <span class="channel-badge configured-badge">
              <i class="fa-solid fa-check"></i>
              Configurado
            </span>
          </div>
          <p-button 
            label="Vista Previa" 
            icon="pi pi-eye" 
            (onClick)="showEmailPreviewModal()"
            styleClass="p-button-outlined p-button-sm">
          </p-button>
        </div>
        
        <div class="channel-content">
          <div class="content-field">
            <label class="field-label">
              <i class="fa-solid fa-heading"></i>
              Asunto
            </label>
            <div class="field-value">{{ template.emailTemplate.subject }}</div>
          </div>

          <div class="content-field">
            <label class="field-label">
              <i class="fa-solid fa-code"></i>
              Contenido HTML
            </label>
            <div class="field-value code-preview">
              <pre>{{ template.emailTemplate.html.substring(0, 300) }}{{ template.emailTemplate.html.length > 300 ? '...' : '' }}</pre>
            </div>
          </div>
        </div>
      </div>

      <!-- SMS TEMPLATE -->
      <div *ngIf="hasSmsTemplate()" class="card-style channel-card sms-card">
        <div class="channel-header">
          <div class="channel-title">
            <div class="channel-icon sms-icon">
              <i class="fa-solid fa-message"></i>
            </div>
            <h3>SMS</h3>
            <span class="channel-badge configured-badge">
              <i class="fa-solid fa-check"></i>
              Configurado
            </span>
          </div>
        </div>
        
        <div class="channel-content">
          <div class="content-field">
            <label class="field-label">
              <i class="fa-solid fa-comment-dots"></i>
              Mensaje (max 160 caracteres)
            </label>
            <div class="field-value sms-preview">
              <div class="sms-bubble">
                {{ template.smsTemplate!.message }}
              </div>
              
              <!-- Contador inteligente con sanitización -->
              <app-sms-character-counter 
                [message]="template.smsTemplate!.message">
              </app-sms-character-counter>
            </div>
          </div>
        </div>
      </div>

      <!-- WHATSAPP TEMPLATE -->
      <div *ngIf="hasWhatsappTemplate()" class="card-style channel-card whatsapp-card">
        <div class="channel-header">
          <div class="channel-title">
            <div class="channel-icon whatsapp-icon">
              <i class="fa-brands fa-whatsapp"></i>
            </div>
            <h3>WhatsApp</h3>
            <span class="channel-badge configured-badge">
              <i class="fa-solid fa-check"></i>
              Configurado
            </span>
          </div>
        </div>
        
        <div class="channel-content">
          <div *ngIf="template.whatsappTemplate!.templateName" class="content-field">
            <label class="field-label">
              <i class="fa-solid fa-tag"></i>
              Nombre del Template de WhatsApp Business
            </label>
            <div class="field-value template-name">
              {{ template.whatsappTemplate!.templateName }}
            </div>
          </div>

          <div class="content-field">
            <label class="field-label">
              <i class="fa-solid fa-comment-dots"></i>
              Mensaje
            </label>
            <div class="field-value whatsapp-preview">
              <div class="whatsapp-bubble">
                {{ template.whatsappTemplate!.message }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- CALL SCRIPT -->
      <div *ngIf="hasCallScript()" class="card-style channel-card call-card">
        <div class="channel-header">
          <div class="channel-title">
            <div class="channel-icon call-icon">
              <i class="fa-solid fa-phone"></i>
            </div>
            <h3>Llamada Telefónica</h3>
            <span class="channel-badge configured-badge">
              <i class="fa-solid fa-check"></i>
              Configurado
            </span>
          </div>
        </div>
        
        <div class="channel-content call-script-content">
          <div class="script-section">
            <div class="script-step">
              <div class="step-number">1</div>
              <div class="step-content">
                <label class="field-label">
                  <i class="fa-solid fa-handshake"></i>
                  Saludo Inicial
                </label>
                <div class="field-value">{{ template.callScript!.greeting }}</div>
              </div>
            </div>

            <div class="script-arrow">
              <i class="fa-solid fa-arrow-down"></i>
            </div>

            <div class="script-step">
              <div class="step-number">2</div>
              <div class="step-content">
                <label class="field-label">
                  <i class="fa-solid fa-message-lines"></i>
                  Mensaje Principal
                </label>
                <div class="field-value">{{ template.callScript!.mainMessage }}</div>
              </div>
            </div>

            <div class="script-arrow">
              <i class="fa-solid fa-arrow-down"></i>
            </div>

            <div class="script-step">
              <div class="step-number">3</div>
              <div class="step-content">
                <label class="field-label">
                  <i class="fa-solid fa-hand-wave"></i>
                  Despedida
                </label>
                <div class="field-value">{{ template.callScript!.closing }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- AUTO CALL CONFIG (Llamada Automatizada) -->
      <div *ngIf="hasAutoCallConfig()" class="card-style channel-card auto-call-card">
        <div class="channel-header">
          <div class="channel-title">
            <div class="channel-icon auto-call-icon">
              <i class="fa-solid fa-robot"></i>
            </div>
            <h3>Llamada Automatizada</h3>
            <span class="channel-badge configured-badge">
              <i class="fa-solid fa-check"></i>
              Configurado
            </span>
          </div>
        </div>
        
        <div class="channel-content">
          <!-- Tipo de llamada -->
          <div class="content-field">
            <label class="field-label">
              <i class="fa-solid fa-cog"></i>
              Tipo de Mensaje
            </label>
            <div class="field-value">
              <span class="call-type-badge" [ngClass]="{
                'tts-badge': template.autoCallConfig!.callType === 'TTS',
                'audio-badge': template.autoCallConfig!.callType === 'AUDIO'
              }">
                <i [class]="template.autoCallConfig!.callType === 'TTS' ? 'fa-solid fa-comment-dots' : 'fa-solid fa-file-audio'"></i>
                {{ template.autoCallConfig!.callType === 'TTS' ? 'Texto a Voz (TTS)' : 'Audio Pregrabado' }}
              </span>
            </div>
          </div>

          <!-- Si es TTS, mostrar el mensaje -->
          <div *ngIf="template.autoCallConfig!.callType === 'TTS'" class="content-field">
            <label class="field-label">
              <i class="fa-solid fa-microphone"></i>
              Mensaje de Voz
            </label>
            <div class="field-value tts-message">
              <div class="tts-bubble">
                {{ template.autoCallConfig!.ttsMessage }}
              </div>
            </div>
          </div>

          <!-- Si es AUDIO, mostrar info del archivo -->
          <div *ngIf="template.autoCallConfig!.callType === 'AUDIO'" class="content-field">
            <label class="field-label">
              <i class="fa-solid fa-file-audio"></i>
              Archivo de Audio
            </label>
            <div class="field-value audio-info">
              <div class="audio-file-display">
                <i class="fa-solid fa-volume-high"></i>
                <span class="audio-filename">{{ template.autoCallConfig!.audioFileName || 'audio.mp3' }}</span>
                <a *ngIf="template.autoCallConfig!.audioUrl" 
                   [href]="template.autoCallConfig!.audioUrl" 
                   target="_blank" 
                   class="audio-link">
                  <i class="fa-solid fa-external-link"></i>
                  Escuchar
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- CANALES NO CONFIGURADOS -->
      <div class="not-configured-channels">
        <div *ngIf="!hasEmailTemplate()" class="not-configured-item">
          <i class="fa-solid fa-envelope"></i>
          <span>Email no configurado</span>
        </div>
        <div *ngIf="!hasSmsTemplate()" class="not-configured-item">
          <i class="fa-solid fa-message"></i>
          <span>SMS no configurado</span>
        </div>
        <div *ngIf="!hasWhatsappTemplate()" class="not-configured-item">
          <i class="fa-brands fa-whatsapp"></i>
          <span>WhatsApp no configurado</span>
        </div>
        <div *ngIf="!hasCallScript() && !hasAutoCallConfig()" class="not-configured-item">
          <i class="fa-solid fa-phone"></i>
          <span>Llamada no configurada</span>
        </div>
      </div>
    </div>
  </ng-container>
</div>

<!-- MODAL DE PREVIEW DE EMAIL -->
<p-dialog 
  [(visible)]="showEmailPreview" 
  [modal]="true" 
  [closable]="true"
  [style]="{ width: '90vw', maxWidth: '900px', maxHeight: '85vh' }"
  header="Vista Previa de Email"
  styleClass="email-preview-modal">
  
  <div class="email-preview-content">
    <div class="preview-header">
      <div class="preview-info">
        <div class="email-field">
          <strong>De:</strong>
          <span class="email-value">Rendafin noreply@rendafin.com</span>
        </div>
        
        <div class="email-field">
          <strong>Para:</strong>
          <span class="email-value">{{ sampleVariables['clientEmail'] }}</span>
        </div>
        
        <div class="email-divider"></div>
        
        <div class="email-field">
          <strong>Asunto:</strong>
          <span class="email-value">{{ replaceVariables(template?.emailTemplate?.subject || '') }}</span>
        </div>
      </div>
    </div>
    
    <div class="preview-body" [innerHTML]="previewEmailHtml"></div>
  </div>

  <ng-template pTemplate="footer">
    <p-button 
      label="Cerrar" 
      icon="pi pi-times" 
      (onClick)="closeEmailPreview()"
      styleClass="p-button-secondary">
    </p-button>
  </ng-template>
</p-dialog>

<!-- DIALOG PARA EDITAR PLANTILLA -->
<p-dialog 
  [(visible)]="showEditDialog" 
  [style]="{ width: '90vw', maxWidth: '1600px' }" 
  header="Editar Plantilla" 
  [modal]="true" 
  styleClass="p-fluid"
  (onHide)="onEditDialogHide()"
>
  <ng-template pTemplate="content">
    <app-template-form></app-template-form>
  </ng-template>
  
  <ng-template pTemplate="footer">
    <p-button 
      label="Cancelar" 
      icon="pi pi-times" 
      (onClick)="hideEditDialog()" 
      styleClass="p-button-text">
    </p-button>
    <p-button 
      label="Guardar" 
      icon="pi pi-check" 
      (onClick)="saveEditedTemplate()">
    </p-button>
  </ng-template>
</p-dialog>

<!-- TOAST MESSAGES -->
<p-toast position="top-right"></p-toast>
